"use client";

import clsx from "clsx";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {type ArtifactData, useArtifactPanel, useAutoOpenArtifact} from "@/contexts/chat/ArtifactPanelContext";
import {useArtifactDownload} from "@/hooks/useArtifactDownload";
import {getFileExtension, hasExtensionSuffix} from "@/utils/fileHelpers";
import Icon, {ICON_SIZES} from "../library/Icon";
import ArtifactCardPreview from "./ask-file-bubble/ArtifactCardPreview";
import FileBubbleFrame from "./ask-file-bubble/FileBubbleFrame";

/**
 * Renders a server-generated file (e.g. produced by an assistant skill) as an
 * expandable preview card. The whole card is clickable: clicking opens the
 * chat-scoped artifact panel. Download / more-actions live in a dropdown in
 * the header to keep the primary click target clean.
 */
const AskGeneratedFileBubble: React.FC<{
   isUser?: boolean;
   agentName?: string;
   brandOverride?: string;
   className?: string;
   downloadUrl: string;
   fileName: string;
   mimeType: string;
   /** Stable backend-issued key for this artifact. Preferred over downloadUrl for identity because signed URLs rotate. */
   objectPath?: string;
   isLastInGroup?: boolean;
   autoOpen?: boolean;
   hideAgentHeader?: boolean;
   feedbackSlot?: React.ReactNode;
   copyText?: string;
}> = ({
   isUser,
   agentName,
   brandOverride,
   className,
   downloadUrl,
   fileName,
   mimeType,
   objectPath,
   isLastInGroup = true,
   autoOpen = false,
   hideAgentHeader = false,
   feedbackSlot,
   copyText,
}) => {
   const {t} = useTranslation("ask");
   const {t: ct} = useTranslation("common");
   const {openArtifact} = useArtifactPanel();
   const triggerDownload = useArtifactDownload();
   const [menuOpen, setMenuOpen] = useState(false);

   const cleanName = fileName.replace("📎 ", "");
   const extension = getFileExtension(cleanName, mimeType);
   const showExtensionCaption = !hasExtensionSuffix(cleanName, extension);
   // Prefer the stable objectPath — signed downloadUrls rotate on each GET /messages,
   // which would force the panel to remount the preview on every poll.
   const artifactId = objectPath ?? `${downloadUrl}:${cleanName}`;

   const buildArtifact = (): ArtifactData => ({
      kind: "generated",
      id: artifactId,
      fileName: cleanName,
      mimeType,
      downloadUrl,
   });

   const openInPanel = () => openArtifact(buildArtifact());

   useAutoOpenArtifact(autoOpen && !!downloadUrl, artifactId, buildArtifact, openArtifact);

   const handleCardClick = (e: React.MouseEvent) => {
      // Ignore clicks that originated on the menu trigger. The menu items
      // themselves render in a portal under document.body, so their events
      // never bubble through this card.
      const target = e.target as HTMLElement;
      if (target.closest("[data-artifact-menu-trigger]")) return;
      openInPanel();
   };

   const handleCardKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
         e.preventDefault();
         openInPanel();
      }
   };

   return (
      <FileBubbleFrame
         isUser={isUser}
         agentName={agentName}
         brandOverride={brandOverride}
         className={className}
         isLastInGroup={isLastInGroup}
         hideAgentHeader={hideAgentHeader}
         feedbackSlot={feedbackSlot}
         copyText={copyText}
      >
         <div
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            aria-label={t("a11y.filePreview")}
            className={clsx(
               "group relative block overflow-hidden rounded-xl border border-300",
               "bg-panel hover:border-primary/40 hover:shadow-md transition-all",
               "cursor-pointer w-full max-w-[620px]",
               "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            )}
         >
            <div className="flex items-center gap-3 px-4 py-2 border-b border-300">
               <div className="flex-1 min-w-0">
                  <p className="font-medium text-900 text-sm truncate" title={cleanName}>
                     {cleanName}
                  </p>
                  {showExtensionCaption && (
                     <p className="text-[10px] text-500 uppercase tracking-wide mt-0.5">
                        {extension}
                     </p>
                  )}
               </div>

               <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger
                     data-artifact-menu-trigger
                     aria-label={t("a11y.moreActions", {defaultValue: "More actions"})}
                     onClick={(e) => e.stopPropagation()}
                     className={clsx(
                        "inline-flex items-center justify-center h-8 w-8 rounded-md",
                        "text-600 hover:text-900 hover:bg-200 transition-colors"
                     )}
                  >
                     <Icon icon="ellipsis" size={ICON_SIZES.SM} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                     <DropdownMenuItem
                        onSelect={() => triggerDownload(downloadUrl, cleanName)}
                        className="flex items-center gap-2 cursor-pointer"
                     >
                        <Icon icon="download" size={ICON_SIZES.SM} />
                        <span>{ct("download", {defaultValue: "Download"})}</span>
                     </DropdownMenuItem>
                     <DropdownMenuItem
                        onSelect={() => openInPanel()}
                        className="flex items-center gap-2 cursor-pointer"
                     >
                        <Icon icon="arrow-pointer" size={ICON_SIZES.SM} />
                        <span>{ct("preview", {defaultValue: "Preview"})}</span>
                     </DropdownMenuItem>
                  </DropdownMenuContent>
               </DropdownMenu>
            </div>

            <div className="relative max-h-[260px] overflow-hidden bg-100/40 px-4 py-2">
               <ArtifactCardPreview url={downloadUrl} fileName={cleanName} mimeType={mimeType} />
               {/* Fade the bottom of the peek so it suggests "more content inside". */}
               <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-panel"
               />
            </div>
         </div>
      </FileBubbleFrame>
   );
};

export default AskGeneratedFileBubble;
